import React, { useCallback, useContext, useEffect } from 'react';
import { Animated, LayoutChangeEvent, Pressable, StyleProp, TextInputProps, TextStyle, ViewStyle } from 'react-native';
import { useToggle } from '../../../hooks/useToggle';
import Text, { textVariantStyleMap } from '../Text';
import { colors } from '../../../utils/theme';
import { getSpacing } from '../../../utils/spacing';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import { View } from 'react-native';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import getBrowser from '../../../utils/getBrowser';
import { ForceStackFieldsContext } from '../Form/ForcedStackFieldsContext';

export type RenderInputFieldArgs = {
    onFocus: () => void;
    onBlur: NonNullable<TextInputProps['onBlur']>;
    hasFocus: boolean;
};

export type InputBaseOwnProps = {
    title: string;
    error?: string;
    value: string | null;
    onBlur?: TextInputProps['onBlur'];
    onFocus?: () => void;
    isDisabled?: boolean;
    testID?: string;
    renderInputField: (args: RenderInputFieldArgs) => React.ReactNode;
    onInputPress: () => void;
    onLayout?: (event: LayoutChangeEvent) => void;
    /**
     * If using masking, provide the placeholder text.
     *
     * The placeholder value may be truthy even if the value is empty.
     */
    maskPlaceholder?: string;
    isRequired?: boolean;
    /**
     * If true, the root Pressable will be disabled onBlur.
     */
    disableRootPressableOnBlur?: boolean;
    forceFullWidth?: boolean;
    staticInputDisplay?: string;
    inputRef?: any;
    helpText?: string;
    /**
     * Minimum height for the value container that sits below the floating label. Use when the value is
     * taller than a line of text (e.g. a row of avatars) so it renders below the label instead of riding
     * up over it — on native the container otherwise collapses and the value overlaps the label.
     */
    valueContainerMinHeight?: number;
};

export const StaticInputField: React.FC<{ value: string | null }> = ({ value }) => {
    return <Text variant="input-text">{value || ''}</Text>;
};

const browser = getBrowser(navigator.userAgent);

export const innerTextInputStyles: StyleProp<TextStyle> = {
    ...textVariantStyleMap['input-text'],
    flex: 1,
    borderColor: 'transparent',
    ...({ outlineStyle: 'none' } as any),
    ...({ fontSynthesis: browser === 'Safari' ? 'style' : undefined } as any),
};

export type InputBaseProps = InputBaseOwnProps;

const animationDuration = 200;

export const containerLayout = {
    leftPadding: getSpacing(4),
    rightPadding: getSpacing(4),
    bottomPadding: getSpacing(4),
    topPadding: getSpacing(2.5),
    minHeight: 50,
};

export const inputContainerStyle: { desktop: StyleProp<ViewStyle>; mobile: StyleProp<ViewStyle> } = {
    desktop: {
        width: '100%',
        maxWidth: 300,
    },
    mobile: {
        width: '100%',
    },
};

const labelUnfocusedStyle = textVariantStyleMap['input-text'];
const labelFocusedStyle = textVariantStyleMap['input-label'];

const labelUnfocusedTop = 50 - (containerLayout.bottomPadding + labelUnfocusedStyle.lineHeight);
const labelFocusedTop = containerLayout.topPadding;

const InputBase: React.FC<InputBaseProps> = (props) => {
    const forceStackFields = useContext(ForceStackFieldsContext);
    const isMobile = useIsMobileLayout();
    const isDisabled = useIsDisabledByParent(props.isDisabled);
    const isFocusedToggle = useToggle(false);
    const hasUserInputValue = !!props.value && props.maskPlaceholder !== props.value;
    const focusedAnimationRef = React.useRef(new Animated.Value(hasUserInputValue ? 1 : 0));
    useEffect(() => {
        Animated.timing(focusedAnimationRef.current, {
            toValue: isFocusedToggle.value || hasUserInputValue ? 1 : 0,
            duration: animationDuration,
            useNativeDriver: false,
        }).start();
    }, [isFocusedToggle.value, hasUserInputValue]);
    const labelStyle = {
        position: 'absolute',
        left: containerLayout.leftPadding,
        fontFamily: labelUnfocusedStyle.fontFamily,
        top: focusedAnimationRef.current.interpolate({
            inputRange: [0, 1],
            outputRange: [labelUnfocusedTop, labelFocusedTop],
        }),
        fontSize: focusedAnimationRef.current.interpolate({
            inputRange: [0, 1],
            outputRange: [labelUnfocusedStyle.fontSize, labelFocusedStyle.fontSize],
        }),
        color: focusedAnimationRef.current.interpolate({
            inputRange: [0, 1],
            outputRange: [labelUnfocusedStyle.color, labelFocusedStyle.color],
        }),
        letterSpacing: focusedAnimationRef.current.interpolate({
            inputRange: [0, 1],
            outputRange: [labelUnfocusedStyle.letterSpacing, labelFocusedStyle.letterSpacing],
        }),
    } as const;

    const onFocus = useCallback(() => {
        isFocusedToggle.toggleOn();
        props.onFocus?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocusedToggle.toggleOn, props.onFocus]);
    const onBlur = useCallback<NonNullable<TextInputProps['onBlur']>>(
        (e) => {
            isFocusedToggle.toggleOff();
            props.onBlur?.(e);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFocusedToggle.toggleOff, props.onBlur],
    );

    return (
        <Pressable
            style={
                isMobile || props.forceFullWidth || forceStackFields
                    ? inputContainerStyle.mobile
                    : inputContainerStyle.desktop
            }
            onPress={() => {
                if (!isDisabled) {
                    isFocusedToggle.toggleOn();
                    props.onInputPress();
                }
            }}
            onLayout={props.onLayout}
            // onFocus/onBlur to handle tab button field traversal
            onFocus={() => {
                onFocus();
                props.onInputPress();
            }}
            onBlur={props.disableRootPressableOnBlur ? undefined : (onBlur as any)}
        >
            <Animated.View
                style={{
                    minHeight: containerLayout.minHeight,
                    backgroundColor: isDisabled ? colors.White : colors['Grey-Light2'],
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    paddingLeft: containerLayout.leftPadding,
                    paddingRight: containerLayout.rightPadding,
                    paddingBottom: containerLayout.bottomPadding,
                    paddingTop: containerLayout.topPadding,
                    width: '100%',
                    borderBottomColor: !!props.error ? colors.Alert : colors.Dividers,
                    borderBottomWidth: 1,
                }}
                onLayout={props.onLayout}
                ref={props.inputRef}
            >
                <Animated.Text style={labelStyle}>{`${props.title}${props.isRequired ? '*' : ''}`}</Animated.Text>
                <View
                    style={{
                        width: '100%',
                        marginTop: 16,
                        // When a value container min height is provided (e.g. the avatar row in the profile
                        // filter), size to content instead of flex:1. On native a flex:1 / flex-basis:0 child
                        // contributes no height to the field, so the field never grows and tall content
                        // overflows up over the floating label. Content sizing lets the field grow to fit.
                        flex: props.valueContainerMinHeight ? undefined : 1,
                        justifyContent: 'flex-end',
                        minHeight: props.valueContainerMinHeight,
                    }}
                >
                    {isDisabled ? (
                        <StaticInputField value={props.staticInputDisplay || props.value} />
                    ) : (
                        props.renderInputField({
                            onFocus,
                            onBlur,
                            hasFocus: isFocusedToggle.value,
                        })
                    )}
                </View>
            </Animated.View>
            {!!(props.error || props.helpText) ? (
                <View style={{ paddingLeft: containerLayout.leftPadding, paddingRight: containerLayout.rightPadding }}>
                    <Text
                        variant={props.error ? 'input-error' : 'input-help-text'}
                        style={{ marginTop: getSpacing(1) }}
                    >
                        {props.error || props.helpText}
                    </Text>
                </View>
            ) : null}
        </Pressable>
    );
};

export default React.memo(InputBase);
